import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, FileText, Loader2, Paperclip, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICE_CATEGORIES, PROPERTY_TYPES } from "@/lib/services";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400";
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
const MAX_MB = 10;

export default function QuoteForm({ source = "quote" }) {
    const isContractor = source === "contractor";
    const [params] = useSearchParams();
    const prefill = params.get("service");
    const mounted = useRef(Date.now());
    const [form, setForm] = useState({
        name: "", email: "", phone: "", address: "",
        property_type: isContractor ? "New Construction" : "",
        services: prefill ? [prefill] : [],
        project_size: "", start_date: "", message: "",
        consent: false, website_hp: "",
    });
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [done, setDone] = useState(null);
    const [errors, setErrors] = useState({});

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const toggleService = (name) =>
        setForm((f) => ({
            ...f,
            services: f.services.includes(name) ? f.services.filter((s) => s !== name) : [...f.services, name],
        }));

    const addFiles = (e) => {
        const incoming = Array.from(e.target.files || []);
        const accepted = [];
        for (const f of incoming) {
            if (files.length + accepted.length >= 5) {
                toast.error("Up to 5 files per request.");
                break;
            }
            if (!ALLOWED.includes(f.type)) {
                toast.error(`${f.name}: only JPG, PNG, WebP, GIF or PDF.`);
                continue;
            }
            if (f.size > MAX_MB * 1024 * 1024) {
                toast.error(`${f.name} is over ${MAX_MB}MB.`);
                continue;
            }
            accepted.push(f);
        }
        if (accepted.length) {
            setFiles((cur) => [...cur, ...accepted]);
            setPreviews((cur) => [...cur, ...accepted.map((f) => ({ name: f.name, type: f.type, url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }))]);
        }
        e.target.value = "";
    };

    const removeFile = (idx) => {
        setFiles((cur) => cur.filter((_, i) => i !== idx));
        setPreviews((cur) => {
            const [p] = cur.filter((_, i) => i === idx);
            if (p?.url) URL.revokeObjectURL(p.url);
            return cur.filter((_, i) => i !== idx);
        });
    };

    const validate = () => {
        const er = {};
        if (!form.name.trim()) er.name = true;
        if (!/^\S+@\S+\.\S+$/.test(form.email)) er.email = true;
        if (!form.phone.trim()) er.phone = true;
        if (!isContractor && form.services.length === 0) er.services = true;
        if (!form.consent) er.consent = true;
        setErrors(er);
        return Object.keys(er).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fill in the highlighted fields.");
            return;
        }
        setLoading(true);
        try {
            let attachmentIds = [];
            if (files.length) {
                setUploading(true);
                for (const f of files) {
                    const fd = new FormData();
                    fd.append("file", f);
                    const { data: up } = await api.post("/uploads", fd);
                    attachmentIds.push(up.id);
                }
                setUploading(false);
            }
            const { data } = await api.post("/leads", {
                ...form,
                source,
                attachment_ids: attachmentIds,
                elapsed_ms: Date.now() - mounted.current,
            });
            setDone(data);
            toast.success(isContractor
                ? "Inquiry received — we'll get back to you within one business day."
                : "Quote request received — we'll contact you within 24 hours.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setUploading(false);
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div data-testid="quote-success-panel" className="border border-gold/40 bg-ink-3 p-8 lg:p-10">
                <CheckCircle2 size={40} className="text-gold" />
                <h3 className="font-display mt-5 text-2xl font-bold">
                    Thank you, {form.name.split(" ")[0]} — your {isContractor ? "inquiry" : "request"} is in.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                    Your reference is <span className="font-mono text-gold">{done.lead_ref}</span>.
                    {isContractor
                        ? " We'll review the project details and respond within one business day with next steps."
                        : " We'll contact you within 24 hours to discuss your project and book your free written estimate."}
                </p>
                {done.attachments > 0 && (
                    <p className="mt-3 text-xs text-slate-400">
                        {done.attachments} attachment{done.attachments > 1 ? "s" : ""} received securely.
                    </p>
                )}
                <p className="mt-4 text-xs text-slate-500">
                    Questions in the meantime? Call{" "}
                    <a href="tel:+16475104012" className="text-gold" data-testid="quote-success-phone-link">647 510 4012</a>
                    {" "}or email{" "}
                    <a href="mailto:Bnbpaintinginc@gmail.com" className="text-gold" data-testid="quote-success-email-link">Bnbpaintinginc@gmail.com</a>.
                </p>
            </div>
        );
    }

    const busy = loading || uploading;

    return (
        <form onSubmit={submit} data-testid="quote-form" noValidate className="space-y-5">
            {/* honeypot — hidden from humans, catches bots */}
            <input
                type="text" name="website" value={form.website_hp} onChange={set("website_hp")}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="absolute h-0 w-0 opacity-0"
            />
            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <label className={labelCls} htmlFor="qf-name">
                        {isContractor ? "Company & contact name *" : "Full name *"}
                    </label>
                    <input id="qf-name" data-testid="quote-input-name" className="field" placeholder={isContractor ? "Acme Homes — John Smith" : "Jane Smith"} value={form.name} onChange={set("name")} aria-invalid={!!errors.name} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="qf-phone">Phone number *</label>
                    <input id="qf-phone" data-testid="quote-input-phone" className="field" type="tel" placeholder="(647) 555-0100" value={form.phone} onChange={set("phone")} aria-invalid={!!errors.phone} />
                </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <label className={labelCls} htmlFor="qf-email">Email address *</label>
                    <input id="qf-email" data-testid="quote-input-email" className="field" type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="qf-address">Project address or city</label>
                    <input id="qf-address" data-testid="quote-input-address" className="field" placeholder="City or full address in the GTA" value={form.address} onChange={set("address")} />
                </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
                <div>
                    <label className={labelCls} htmlFor="qf-type">Type of property</label>
                    <select id="qf-type" data-testid="quote-select-property" className="field" value={form.property_type} onChange={set("property_type")}>
                        <option value="">Select…</option>
                        {PROPERTY_TYPES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="qf-size">
                        {isContractor ? "Approximate square footage" : "Approximate project size"}
                    </label>
                    <input id="qf-size" data-testid="quote-input-size" className="field" placeholder={isContractor ? "e.g. 3,400 sq ft" : "Sq ft or number of rooms"} value={form.project_size} onChange={set("project_size")} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="qf-date">{isContractor ? "Target timeline" : "Preferred start date"}</label>
                    <input id="qf-date" data-testid="quote-input-date" className="field" type={isContractor ? "text" : "date"} placeholder={isContractor ? "e.g. 6-8 weeks out" : ""} value={form.start_date} onChange={set("start_date")} />
                </div>
            </div>
            {!isContractor && (
                <div>
                    <span className={`${labelCls} ${errors.services ? "text-red-400" : ""}`}>Painting services required * (select all that apply)</span>
                    <div className="flex flex-wrap gap-2" data-testid="quote-services-chips">
                        {SERVICE_CATEGORIES.map((s) => (
                            <button
                                type="button" key={s.name} data-testid={`quote-service-chip-${s.short.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => toggleService(s.name)}
                                className={`border px-3.5 py-2 text-xs font-medium transition-colors ${
                                    form.services.includes(s.name)
                                        ? "border-gold bg-gold/15 text-gold"
                                        : "border-white/15 text-slate-300 hover:border-gold/50"
                                }`}
                            >
                                {s.short}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div>
                <label className={labelCls} htmlFor="qf-message">
                    {isContractor ? "Project details, drawings & scope" : "Describe the work"}
                </label>
                <textarea id="qf-message" data-testid="quote-input-message" className="field min-h-28" placeholder={isContractor ? "Scope, units, floors, site details…" : "Rooms, surfaces, colours, timeline, anything on your mind…"} value={form.message} onChange={set("message")} />
            </div>
            <div>
                <span className={labelCls}>
                    <Paperclip size={11} className="mr-1 inline" /> Photos or drawings (optional)
                </span>
                <label
                    htmlFor="qf-files" data-testid="quote-attachments-dropzone"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-gold/40 bg-gold/5 px-4 py-6 text-center transition-colors hover:border-gold hover:bg-gold/10"
                >
                    <span className="text-sm text-slate-300">Click to add photos of the space or drawings (JPG, PNG, WebP, PDF)</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Up to 5 files · max 10MB each · stored securely</span>
                </label>
                <input id="qf-files" data-testid="quote-attachments-input" type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" className="sr-only" onChange={addFiles} />
                {previews.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="quote-attachments-previews">
                        {previews.map((p, i) => (
                            <div key={i} data-testid={`quote-attachment-chip-${i}`} className="relative flex items-center gap-2 border border-gold/25 bg-ink-3 p-2 pr-9 text-xs text-slate-300">
                                {p.url ? (
                                    <img src={p.url} alt={p.name} className="h-10 w-10 object-cover" />
                                ) : (
                                    <FileText size={18} className="text-gold" />
                                )}
                                <span className="max-w-32 truncate">{p.name}</span>
                                <button type="button" onClick={() => removeFile(i)} data-testid={`quote-attachment-remove-${i}`}
                                    aria-label={`Remove ${p.name}`}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-red-400">
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-slate-400">
                <input
                    type="checkbox" data-testid="quote-consent-checkbox" checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059]"
                />
                <span>
                    I agree to be contacted by B&amp;B Painting and Renovation about this inquiry by
                    phone or email. <span className={errors.consent ? "text-red-400" : ""}>Required.</span>
                </span>
            </label>
            <button
                type="submit" data-testid="quote-submit-button" disabled={busy}
                className="group inline-flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto"
            >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : busy ? <Loader2 size={16} className="animate-spin" /> : null}
                {uploading ? "Uploading photos…" : loading ? "Sending…" : isContractor ? "Request Subcontractor Quote" : "Request a Free Quote"}
                {!busy && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
            </button>
        </form>
    );
}